import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { PageLoading, PageError } from '@/components/ui/PageAsyncState'
import { useCan } from '@/features/auth/AuthContext'
import {
  createSalesOrderApi,
  searchVariantsApi,
  type VariantSearchResult,
} from '@/services/salesOrders'
import { listCustomersApi, getCustomerApi, type Customer, type CustomerBuyer } from '@/services/customers'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import {
  type GridLineItem,
  createEmptyGridLine,
  validateGridLine,
  buildPatchLinesPayload,
} from './salesOrderGridUtils'
import { SalesOrderGrid } from './SalesOrderGrid'
import { PasteExcelModal } from './PasteExcelModal'

export interface SalesOrderCreatePageProps {
  initialLoading?: boolean
}

export function SalesOrderCreatePage({ initialLoading = false }: SalesOrderCreatePageProps = {}): React.ReactElement {
  const navigate = useNavigate()
  const canWrite = useCan('sales.write')

  const [title, setTitle] = useState(`SO-${new Date().getFullYear()}-TẠO-MỚI`)
  const [lines, setLines] = useState<GridLineItem[]>([createEmptyGridLine()])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [customerBuyersMap, setCustomerBuyersMap] = useState<Record<string, CustomerBuyer[]>>({})
  const [variantsPreload, setVariantsPreload] = useState<VariantSearchResult[]>([])

  const [loading, setLoading] = useState(initialLoading)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pasteModalOpen, setPasteModalOpen] = useState(false)
  const [errorHighlightFields, setErrorHighlightFields] = useState<{
    lineId: string
    fields: string[]
  } | null>(null)

  // Load active customers and variants
  useEffect(() => {
    let cancelled = false
    setLoading(true)

    Promise.all([listCustomersApi({ status: 'all' }), searchVariantsApi('')])
      .then(([custList, varList]) => {
        if (cancelled) return
        setCustomers(custList)
        setVariantsPreload(varList)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Không thể tải danh mục')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Load buyers for customers when added to lines
  const loadCustomerBuyers = useCallback(async (cId: string) => {
    if (!cId || customerBuyersMap[cId]) return
    try {
      const detail = await getCustomerApi(cId)
      if (detail?.buyers) {
        setCustomerBuyersMap((prev) => ({ ...prev, [cId]: detail.buyers }))
      }
    } catch {
      // Ignore single customer failure
    }
  }, [customerBuyersMap])

  useEffect(() => {
    lines.forEach((l) => {
      if (l.customer_id) {
        void loadCustomerBuyers(l.customer_id)
      }
    })
  }, [lines, loadCustomerBuyers])

  const handleUpdateLines = (newLines: GridLineItem[]) => {
    setLines(newLines)
  }

  const handleSave = async () => {
    if (!canWrite) return

    // 1. Validation check
    let firstInvalidLineId: string | null = null
    let invalidCount = 0

    for (const l of lines) {
      const v = validateGridLine(l)
      if (!v.valid) {
        invalidCount++
        if (!firstInvalidLineId) {
          firstInvalidLineId = l.id || l.tempKey
          setErrorHighlightFields({
            lineId: l.id || l.tempKey,
            fields: v.missingFields,
          })
        }
      }
    }

    if (invalidCount > 0) {
      alert(`⚠️ Còn ${invalidCount} dòng chưa điền đủ thông tin bắt buộc (Khách hàng, Mã hàng, Ráp/Tháo, ISTA, Số lượng). Vui lòng kiểm tra lại.`)
      return
    }

    setSaving(true)
    setErrorHighlightFields(null)

    try {
      const patchLinesPayload = buildPatchLinesPayload(lines)
      const created = await createSalesOrderApi({
        title: title.trim() || null,
        lines: patchLinesPayload as any,
      })

      alert('✓ Đã tạo đơn hàng thành công!')
      navigate(`/sales/orders/${created.id}`)
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      alert(`❌ Lỗi khi tạo đơn hàng: ${formatted.message}`)
    } finally {
      setSaving(false)
    }
  }

  const handleImportPastedLines = (pastedLines: any[]) => {
    const newItems: GridLineItem[] = pastedLines.map((p) => {
      const cust = customers.find((c) => c.code.toUpperCase() === p.customer_code.toUpperCase())
      const v = variantsPreload.find((vm) => vm.variant_code.toUpperCase() === p.variant_code.toUpperCase())

      return {
        tempKey: `line-paste-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        customer_id: cust?.id || '',
        customer_code: cust?.code || p.customer_code,
        customer_name: cust?.name || '',
        buyer_id: null,
        buyer_name: null,
        variant_id: v?.id || '',
        variant_code: v?.variant_code || p.variant_code,
        product_name: v?.product_name || '',
        product_id: v?.product_id || '',
        has_image: Boolean(v?.has_image || v?.image_url),
        color_name: v?.color?.name || v?.color_name || '',
        color_swatch_hex: v?.color?.swatch_hex || null,
        w: v?.dimensions?.w ?? v?.w ?? null,
        d: v?.dimensions?.d ?? v?.d ?? null,
        h: v?.dimensions?.h ?? v?.h ?? null,
        dim_unit: 'inch',
        assembly_type: p.assembly_type || '',
        ista_level: p.ista_level || '',
        quantity: p.quantity || 1,
        customer_po: p.customer_po || '',
        pcs_per_carton: null,
        packing_note: '',
        requested_ship_date: '',
        updated_at: '',
        line_status: 'draft',
        status_reason: null,
      }
    })

    setLines((prev) => [...prev, ...newItems])
  }

  if (!canWrite) {
    return (
      <div className="rounded-md border border-rose-300 bg-rose-50 p-6 text-center text-rose-800">
        <p className="font-bold text-base mb-1">Quyền truy cập bị từ chối</p>
        <p className="text-sm">Bạn không có quyền tạo đơn hàng (sales.write).</p>
      </div>
    )
  }

  if (loading) return <PageLoading label="Đang nạp dữ liệu..." />
  if (error) return <PageError message={error} onRetry={() => window.location.reload()} />

  return (
    <div className="space-y-3">
      {/* Breadcrumb: Sale > Đơn hàng > Tạo mới */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sale', to: '/sales' },
          { label: 'Đơn hàng', to: '/sales/orders' },
          { label: 'Tạo mới', to: '/sales/orders/new' },
        ]}
      />

      {/* Title Header Card */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nhập tên bảng đơn hàng..."
            className="text-xl font-extrabold text-slate-900 border-b border-dashed border-slate-400 bg-transparent outline-none focus:border-emerald-600 focus:bg-emerald-50/40 px-1 py-0.5 min-w-[280px]"
            title="Bấm vào để đổi tên bảng đơn hàng"
          />
          <span className="rounded border border-slate-300 bg-slate-100 px-2 py-0.5 font-mono text-xs font-bold text-slate-500">
            SO tự sinh khi lưu
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="text-xs"
            onClick={() => setPasteModalOpen(true)}
          >
            📋 Dán từ Excel
          </Button>

          <Button
            variant="primary"
            size="sm"
            className="text-xs font-bold"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Đang tạo...' : '💾 Tạo đơn hàng'}
          </Button>
        </div>
      </div>

      {/* Excel-style Grid */}
      <SalesOrderGrid
        lines={lines}
        customers={customers}
        customerBuyersMap={customerBuyersMap}
        variantsPreload={variantsPreload}
        canWrite={canWrite}
        canDemandWrite={false}
        onUpdateLines={handleUpdateLines}
        onSave={handleSave}
        saving={saving}
        errorHighlightFields={errorHighlightFields}
      />

      {/* Paste from Excel Modal */}
      <PasteExcelModal
        isOpen={pasteModalOpen}
        onClose={() => setPasteModalOpen(false)}
        onImport={handleImportPastedLines}
      />
    </div>
  )
}
